/* eslint-disable no-console */
import { type LogEntry, LogLevel, LogPurpose } from '../types/common';
import { logFrontend } from './api';

const defaultLogLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel ?? LogLevel.INFO;
const defaultPurpose = import.meta.env.VITE_LOG_PURPOSE as LogPurpose ?? LogPurpose.GENERAL;

function removeCircular(seen: WeakSet<object>, obj?: unknown) {
  if (typeof obj !== 'object' || !obj) {
    return;
  }
  seen.add(obj as object);
  Object.entries(obj).forEach(([key, value]) => {
    if (value === '[Circular]') {
      delete (obj as Record<string, unknown>)[key];
      return;
    }
    if (typeof value !== 'object') {
      return;
    }
    if (seen.has(value)) {
      delete (obj as Record<string, unknown>)[key];
    } else {
      removeCircular(seen, value);
    }
  });
}

class Logger {
  private _defaultLogLevel = defaultLogLevel;

  private _defaultLogPurpose = defaultPurpose;

  get defaultLogLevel(): LogLevel {
    return this._defaultLogLevel;
  }

  set defaultLogLevel(logLevel: LogLevel) {
    this._defaultLogLevel = logLevel;
  }

  get defaultLogPurpose(): LogPurpose {
    return this._defaultLogPurpose;
  }

  set defaultLogPurpose(logPurpose: LogPurpose) {
    this._defaultLogPurpose = logPurpose;
  }

  private _isLoggable(logLevel: LogLevel): boolean {
    return Object.values(LogLevel).indexOf(logLevel)
      >= Object.values(LogLevel).indexOf(this._defaultLogLevel);
  }

  private async _log(entry: LogEntry): Promise<void> {
    if (!entry.message) {
      console.error('Log entry is missing message');
    }

    entry.logLevel = entry.logLevel ?? this._defaultLogLevel;
    entry.purpose = entry.purpose ?? this._defaultLogPurpose;

    if (!this._isLoggable(entry.logLevel)) {
      return;
    }

    if (entry.context) {
      removeCircular(new WeakSet(), entry.context);
    }

    const [error] = await logFrontend(entry);
    if (error) {
      console.error('Failed to log frontend', error);
    }
  }

  async debug(entry: LogEntry): Promise<void> {
    await this._log({ ...entry, logLevel: LogLevel.DEBUG });
  }

  async info(entry: LogEntry): Promise<void> {
    await this._log({ ...entry, logLevel: LogLevel.INFO });
  }

  async warning(entry: LogEntry): Promise<void> {
    await this._log({ ...entry, logLevel: LogLevel.WARNING });
  }

  async error(entry: LogEntry): Promise<void> {
    await this._log({ ...entry, logLevel: LogLevel.ERROR });
  }

  async fatal(entry: LogEntry): Promise<void> {
    await this._log({ ...entry, logLevel: LogLevel.FATAL });
  }
}

const logger = new Logger();

export default logger;
